import * as _ from 'lodash';
import * as qs from 'qs';
import { createSelector } from 'reselect';

import TeamsModule from '../teams';
import type { ProjectsFilter, ProjectsQuery, ProjectsSort } from '../../../shared/projectsV2';
import type { TeamSelection } from '../../types';
import type { ReduxStore } from '../store';

import { DEFAULT_COUNT, DEFAULT_PAGE, DEFAULT_SORT, DEFAULT_TITLE } from './consts';
import { areAllProjectsSelected, isAnyProjectOnPageSelected, isEveryProjectOnPageSelected } from './utils';
import type { HydrateStateQuery, ProjectsV2Store } from './types';

export const getProjectsV2Store = (state: ReduxStore) => state.projectsV2;
const createProjectsV2Selector = <R>(selectorFn: (state: ProjectsV2Store) => R) =>
  createSelector(getProjectsV2Store, selectorFn);

// asyncStatus is divided into sub-fields for each of the various network requests.
export const getAsyncStatus = createProjectsV2Selector((state: ProjectsV2Store) => state.asyncStatus);
export const getCategory = createProjectsV2Selector((state: ProjectsV2Store) => state.category);
export const getAsyncStatusFetchProjects = createSelector(getAsyncStatus, asyncStatus => asyncStatus.fetchProjects);
export const getAsyncStatusUpdateProjects = createSelector(getAsyncStatus, asyncStatus => asyncStatus.updateProjects);
export const getProjects = createProjectsV2Selector((state: ProjectsV2Store) => state.projects);
export const getTotalProjects = createProjectsV2Selector((state: ProjectsV2Store) => state.totalProjects);
export const getSelectedProjects = createProjectsV2Selector((state: ProjectsV2Store) => state.selectedProjects);
export const getOrganizationLabels = createProjectsV2Selector((state: ProjectsV2Store) => state.organizationLabels);
export const getSortedOrganizationLabels = createProjectsV2Selector((state: ProjectsV2Store) =>
  _.sortBy(state.organizationLabels, (label) => label.label.toLowerCase()),
);
export const getTitle = createProjectsV2Selector((state: ProjectsV2Store) => state.title);
export const getSort = createProjectsV2Selector((state: ProjectsV2Store) => state.sort);
export const getPage = createProjectsV2Selector((state: ProjectsV2Store) => state.page);
export const getCount = createProjectsV2Selector((state: ProjectsV2Store) => state.count);
export const getFilter = createProjectsV2Selector((state: ProjectsV2Store) => state.filter);
export const getAsyncStatusCategoriesSummary =
  createSelector(getAsyncStatus, asyncStatus => asyncStatus.fetchCategoriesSummary);
export const getCategoriesSummary = createProjectsV2Selector((state: ProjectsV2Store) => state.categoriesSummary);
export const getBulkLabelData = createProjectsV2Selector((state: ProjectsV2Store) => state.bulkLabelData);

// This grabs the current state so that it can be merged with the query params
export const getHydrateStateQuery = createSelector([
  getCategory,
  getPage,
  getCount,
  getSort,
  getTitle,
  getFilter,
], (category, page, count, sort, title, filter): HydrateStateQuery => ({
  category,
  page,
  count,
  sort,
  title,
  filter,
}));

// Whether title search or any other filter is applied (excluding team selection)
export const getHasFilterApplied = createSelector([
  getFilter,
  getTitle,
], (filters, title): boolean => {
  // returns true if any of the values in `filters` have an object with at least one key, or if title search is truthy
  return Object.values(filters).some(filter => filter !== undefined) || !!title;
});

/**
 * Merge the current search (by title) and team selection into
 * the baseline filter.
 */
export const getProjectsFilterQuery = createSelector(
  [getFilter, getTitle, TeamsModule.selectors.getTeamSelection],
  (filter: ProjectsFilter, title: string, teamSelection: TeamSelection): ProjectsFilter => ({
    ...filter,
    title,
    teamId: _.isEmpty(teamSelection) ? undefined : teamSelection,
  }),
);

/**
 * Maps the state to a valid ProjectsQuery.
 * This is what informs the backend of the current state of the FE,
 * ie: which filters/sorting/pagination are in place.
 */
export const getFetchProjectsQuery = createSelector(
  [getPage, getCount, getSort, getProjectsFilterQuery], (
    page: number,
    count: number,
    sort: ProjectsSort,
    filter: ProjectsFilter,
  ): ProjectsQuery => ({
    page,
    count,
    sort,
    filter,
  }),
);

// Get the "range" display, which is used in ProjectsPaginationCount beside the count selector.
export const getCurrentPageRange = createSelector([
  getPage,
  getCount,
  getTotalProjects,
], (page, count, totalProjects): string | null => {
  if (totalProjects === 0) {
    return null;
  }

  // If there are less project than the page size,
  // it doesn't make sense to calculate + display a range.
  if (totalProjects < count) {
    return `1 - ${totalProjects}`;
  }

  const rangeStart = ((page - 1) * count) + 1;
  const rangeEnd = count * page;
  return `${rangeStart} - ${rangeEnd < totalProjects ? rangeEnd : totalProjects} of ${totalProjects.toLocaleString()}`;
});

// The total number of pages is derived from taking the # of projects
// and dividing it by the current count.
export const getTotalPages = createSelector([
  getTotalProjects,
  getCount,
], (totalProjects, count): number | null => {
  if (totalProjects === 0) {
    return null;
  }

  //  Round up the total # of projects / count.
  return Math.ceil(totalProjects / count);
});

//  Selector which tells us if only SOME projects are selected.
export const getAreSomeProjectsSelected =
  createSelector([getProjects, getSelectedProjects], isAnyProjectOnPageSelected);

//  Selector which tells us if every project on a given page is selected.
export const getIsEveryProjectOnPageSelected = createSelector([
  getProjects,
  getSelectedProjects,
], isEveryProjectOnPageSelected);

//  Tests specifically for the 'all' string on selectedProjects
export const getAreAllProjectsSelected = createSelector(getSelectedProjects, areAllProjectsSelected);

export const getSelectedProjectCount = createSelector([
  getSelectedProjects,
  getTotalProjects,
], (selectedProjects, totalProjectCount) => {
  if (areAllProjectsSelected(selectedProjects)) {
    return totalProjectCount;
  }
  return selectedProjects.length;
});

export const getBulkCheckboxState = createSelector([
  getIsEveryProjectOnPageSelected,
  getAreSomeProjectsSelected,
], (isEveryProjectSelected, someProjectsSelected): 'empty' | 'indeterminate' | 'checked' => {
  if (isEveryProjectSelected) {
    return 'checked';
  }
  if (someProjectsSelected) {
    return 'indeterminate';
  }
  return 'empty';
});

export const getIsProjectSelected = createSelector([getSelectedProjects,
  (_state: ReduxStore, id: string | number) => id,
], (selectedProjects, projectId) =>
  areAllProjectsSelected(selectedProjects) || selectedProjects.some((project) => project.id.toString() === projectId),
);

export const getSelectedProjectLocators = createSelector(getSelectedProjects, (selectedProjects): string[] | 'all' => {
  if (areAllProjectsSelected(selectedProjects)) {
    return selectedProjects;
  }
  return selectedProjects.map((p) => String(p.id));
});

//  Used in the BulkSelectionBanner to determine how many issues are actually on the page.
//  Helps avoid bugs when there are less issues on the page then the count.
export const getProjectsOnPageCount = createSelector(getProjects, projects => projects.length);

/**
 * Constructs a URL query string from the projects state
 * Used to maintain filter state when navigating to the projects page
 * Only returns query string for project scope, empty string for release groups or other categories
 */
export const getProjectsQueryString = createSelector(
  [getHydrateStateQuery],
  (query): string => {
    if (query.category !== 'projects') {
      return '';
    }

    // It is important to account for the default values so we won't append unnecessary params to the query string
    const queryString = qs.stringify({
      title: query.title !== DEFAULT_TITLE ? query.title : undefined,
      sort: query.sort !== DEFAULT_SORT ? query.sort : undefined,
      page: query.page !== DEFAULT_PAGE ? query.page : undefined,
      count: query.count !== DEFAULT_COUNT ? query.count : undefined,
      filter: query.filter,
    });

    return queryString ? `?${queryString}` : '';
  },
);
